import React from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { Link, useRouteMatch } from 'react-router-dom'
import Flex, { FlexItem } from 'styled-flex-component'

import { SubmissionTextThumbnail } from '@components/BrandFans/InfluencerIdentity/Row/Submissions/TextThumbnail'
import { EllipsisWrapper } from '@components/InfluencerIdentity/Row/Details/Details.styled'
import Image from '@components/UI/Image'
import ConnectedTrackedRoute from '@components/UI/TrackedRoute/ConnectedTrackedRoute'
import { AspectRatios, EventTrackingNames, SocialSubmissionTypes } from '@enums'
import { IApplicationState } from '@store'
import {
  selectInfluencerHandle,
  selectInfluencerImageURL,
  selectInfluencerName,
  selectIsCarousel,
  selectPublishedURL,
  selectSocialPageURL,
  selectSubmissionMetrics,
} from '@store/postCampaignReport'
import { selectActivePCRChannel, selectCampaignId } from '@store/router'
import { viewSocial } from '@store/submissions'
import Theme from '@theme'
import { Container, FadeIn, H5, Icon, Link as AnchorLink, Text } from '@tribegroup'
import AnalyticsPostCampaignReportListViewProfileSocialIcon from './SocialIcon'

interface IProps {
  submissionId: number
  backgroundColor: string
}

const AnalyticsPostCampaignReportListViewProfile: React.FC<IProps> = ({
  submissionId,
  backgroundColor,
}) => {
  const match = useRouteMatch()
  const dispatch = useDispatch()

  const campaignId = selectCampaignId(match)!

  const influencerName = useSelector((state: IApplicationState) =>
    selectInfluencerName(state, submissionId),
  )
  const influencerHandle = useSelector((state: IApplicationState) =>
    selectInfluencerHandle(state, submissionId),
  )
  const submissionImageURL = useSelector((state: IApplicationState) =>
    selectInfluencerImageURL(state, submissionId),
  )
  const socialPageURL = useSelector((state: IApplicationState) =>
    selectSocialPageURL(state, submissionId),
  )
  const isCarousel = useSelector((state: IApplicationState) =>
    selectIsCarousel(state, submissionId),
  )
  const activePCRChannel = useSelector((state: IApplicationState) => selectActivePCRChannel(state))
  const publishedURL = useSelector((state: IApplicationState) =>
    selectPublishedURL(state, submissionId),
  )
  const metrics = useSelector((state: IApplicationState) =>
    selectSubmissionMetrics(state, submissionId),
  )

  const onViewSocial = () => dispatch(viewSocial(campaignId, submissionId))

  const isText =
    'social_submission_type' in metrics &&
    metrics.social_submission_type === SocialSubmissionTypes.Text

  return (
    <Container borderRadius={0.5} backgroundColor={backgroundColor} height={4.75}>
      <Flex alignStart alignCenter>
        <FlexItem noShrink>
          <Container
            borderRadius={0.25}
            overflow="hidden"
            width={3.5}
            height={3.5}
            rightOuterSpacing={1.25}
            topOuterSpacing={0.625}
            verticalAlign="middle"
            inlineBlock
          >
            <ConnectedTrackedRoute
              eventName={EventTrackingNames.ViewSubmission}
              campaignId={campaignId}
              submissionId={submissionId}
              additionalProperties={{
                source: 'campaign_metrics',
              }}
            >
              <Link
                to={`/campaigns/${campaignId}/submissions/${submissionId}?from=${activePCRChannel}`}
              >
                <Container relative>
                  {isText ? (
                    <SubmissionTextThumbnail padding="20%" />
                  ) : (
                    <Image
                      src={submissionImageURL}
                      aspectRatio={AspectRatios.ONE_BY_ONE}
                      animation={FadeIn}
                      loadingColor={Theme.grey900Color}
                    />
                  )}
                  {isCarousel && (
                    <Container absolute bottomPosition={0.5} leftPosition={0.5}>
                      <Icon size={1.25} glyph="carousel" color={Theme.whiteColor} />
                    </Container>
                  )}
                  {isText && (
                    <Container absolute bottomPosition={0.5} leftPosition={0.5}>
                      <Icon size={1.25} glyph="text-post" color={Theme.whiteColor} />
                    </Container>
                  )}
                </Container>
              </Link>
            </ConnectedTrackedRoute>
          </Container>
        </FlexItem>
        <Container topInnerSpacing={1.125} rightInnerSpacing={1}>
          <FlexItem>
            <EllipsisWrapper color={Theme.defaultColor}>
              <AnchorLink
                href={publishedURL || socialPageURL}
                target="_blank"
                onClick={onViewSocial}
              >
                <H5 inline color={Theme.defaultColor} rightOuterSpacing={0.75}>
                  {influencerName}
                </H5>
              </AnchorLink>
            </EllipsisWrapper>
            <EllipsisWrapper color={Theme.grey700Color}>
              <AnchorLink
                href={publishedURL || socialPageURL}
                target="_blank"
                onClick={onViewSocial}
              >
                <Container
                  verticalAlign="middle"
                  rightOuterSpacing={0.25}
                  topOuterSpacing={0.125}
                  inlineBlock
                >
                  <AnalyticsPostCampaignReportListViewProfileSocialIcon
                    submissionId={submissionId}
                  />
                </Container>
                <Container rightOuterSpacing={0.5} inline>
                  <Text xsmall color={Theme.grey700Color}>
                    {influencerHandle}
                  </Text>
                </Container>
                <Container inline verticalAlign="bottom">
                  <Icon size={0.75} glyph={'external-link'} color={Theme.grey700Color} />
                </Container>
              </AnchorLink>
            </EllipsisWrapper>
          </FlexItem>
        </Container>
      </Flex>
    </Container>
  )
}

export default AnalyticsPostCampaignReportListViewProfile
